import React, { useMemo, useState } from 'react'
import { Row, Paragraph, Button, colors, Divider, Separator, List, Skeleton, InfoSheetPanel } from '@fs/zion-ui'
import { HelpAi } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import { hasTranscript, useSlsStateContext, getImage } from '@fs/zion-sls'
import { useQueryParams } from '@fs/zion-router'
import { useFullTextStateContext } from '../../../../fullText/providers/FullTextContext'
import FullText from '../../../../fullText/FullText'

/**
 * TranscriptAlertBanner
 *
 * Displays an alert banner when an AI-generated transcript is available for the current image.
 * Only renders if the user has permission and a transcript exists.
 *
 * @component
 * @returns {JSX.Element|null} The banner or null if no transcript or no permission
 */
export default function TranscriptAlertBanner({ fullTextOverlay }) {
  const [t] = useTranslation()
  const fullTextState = useFullTextStateContext()
  const zionSlsState = useSlsStateContext()
  const { setQuery } = useQueryParams()
  const handleViewTranscript = () => setQuery({ view: 'fullText' })
  const [loading, setLoading] = useState(true)

  const currentImage = useMemo(
    () => getImage(zionSlsState.images, zionSlsState.currentImageArk),
    [zionSlsState.images, zionSlsState.currentImageArk]
  )

  const showTranscript = useMemo(() => {
    const transcript = hasTranscript(currentImage?.elements)
    setLoading(false)
    return transcript
  }, [currentImage])

  const userClosedFullTextPanel = () => {
    setQuery({ view: 'index' })
    fullTextOverlay.close()
  }

  if (loading) {
    return (
      <Row alignY="middle">
        <List growX>
          <Skeleton.ListItem hasIcon />
        </List>
        <Skeleton.Button width={72} />
      </Row>
    )
  }

  if (!fullTextState.imagePermission || !showTranscript) return null

  return (
    <>
      <Divider fullWidth />
      <Separator size="xxs" />
      <Row alignX="start" alignY="middle" wrap={false} guttersX="xxs">
        <HelpAi size="xs" color="white" background={colors.purple50} />
        <Paragraph size="sm" growX>
          {t('select-person.transcript-banner', 'AI-generated transcript available.')}
        </Paragraph>

        <Button
          emphasis="low"
          color="blue"
          onClick={handleViewTranscript}
          linkName="View Transcript"
          data-testid="view-transcript-button"
          noShrinkX
        >
          {t('select-person.view-transcript', 'VIEW')}
        </Button>
      </Row>
      <Separator size="xxs" />
      <Divider fullWidth />
      <InfoSheetPanel
        title={t('transcript-alert-banner.view-names', 'View Names')}
        {...fullTextOverlay}
        close={userClosedFullTextPanel}
      >
        <FullText />
      </InfoSheetPanel>
    </>
  )
}
